import {
   createAsyncThunk,
   createEntityAdapter,
   createSelector,
   createSlice,
} from "@reduxjs/toolkit"
import { ApiError, ApiResponse } from "@/api"
import {
   requestSensorsByType,
   requestWearableChargingStatus,
   SensorType,
   WearableChargingStatusResponse,
   WearableSensorResponse,
} from "@/api/SensorsService"
import { HttpCodes } from "@/constants/globals"
import type { RootState } from "@/store"
import { normalizeMacAddress } from "@/utils/index"

const adapter = createEntityAdapter<WearableSensorResponse>()

interface WearableSensorsState {
   loading: boolean
   error: number | null
}

export const initialState: WearableSensorsState &
   ReturnType<typeof adapter.getInitialState> = {
   ...adapter.getInitialState(),
   loading: false,
   error: null,
}

export const fetchWearableSensors = createAsyncThunk(
   "wearableSensors/fetchWearableSensors",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<WearableSensorResponse[]> =
            await requestSensorsByType(SensorType.WEARABLE)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchWearableChargingStatus = createAsyncThunk(
   "wearableSensors/fetchWearableChargingStatus",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<WearableChargingStatusResponse[]> =
            await requestWearableChargingStatus()
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

const wearableSensorsSlice = createSlice({
   name: "wearableSensors",
   initialState,
   reducers: {},
   extraReducers: (builder) => {
      builder
         .addCase(fetchWearableSensors.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(fetchWearableSensors.fulfilled, (state, action) => {
            state.loading = false
            adapter.setAll(state, action.payload.data)
         })
         .addCase(fetchWearableSensors.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
   },
})

const adapterSelectors = adapter.getSelectors(
   (state: RootState) => state.wearableSensorsReducer
)

export const selectAllWearableSensors = adapterSelectors.selectAll
export const selectWearableSensorEntities = adapterSelectors.selectEntities

export const selectUnassignedWearableSensors = createSelector(
   selectAllWearableSensors,
   (wearables) => wearables.filter((wearable) => wearable.inmate === null)
)

export const selectWearableSensorsByMacAddress = createSelector(
   selectAllWearableSensors,
   (wearables) =>
      new Map(
         wearables.map((wearable) => [
            normalizeMacAddress(wearable.macAddress),
            wearable,
         ])
      )
)

export default wearableSensorsSlice.reducer
